import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
  acceptMovementsApi,
  claimMovementsApi,
  fetchMovementAssignmentsApi,
  fetchMovementsApi,
  rejectMovementsApi,
} from '../../../api/movementApi';
import {
  AssignmentStatus,
  CreateMovementPayload,
  FetchMovementAssignmentsPayload,
  FetchMovementsPayload,
  MovementAssignmentsResponseDto,
  MovementsResponseDto,
  UpdateMovementPayload,
} from '../../../common/types';
import { startLoading, stopLoading } from 'src/features/loading';
import { handleErrorSaga } from 'src/utils/handleErrorSaga';
import {
  acceptMovements,
  claimMovements,
  fetchUnassignedMovements,
  fetchProposedMovements,
  rejectMovements,
  setMovements,
} from './movementStore';
import { handleSuccessSaga } from 'src/utils/handleSuccessSaga';

function* fetchMovementsSaga(action: PayloadAction<FetchMovementsPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const movementsResponse: MovementsResponseDto = yield call(fetchMovementsApi, action.payload);
    yield put(
      setMovements({
        type: action.payload.status,
        movements: movementsResponse.responseBody.results,
      }),
    );
  } catch (error) {
    yield call(handleErrorSaga, error as AxiosError);
  } finally {
    yield put(stopLoading());
  }
}

function* fetchMovementAssignmentsSaga(
  action: PayloadAction<FetchMovementAssignmentsPayload>,
): SagaIterator {
  try {
    yield put(startLoading());
    const movementsResponse: MovementAssignmentsResponseDto = yield call(
      fetchMovementAssignmentsApi,
      action.payload,
    );
    yield put(
      setMovements({
        type: AssignmentStatus.PROPOSED,
        movements: movementsResponse.responseBody,
      }),
    );
  } catch (error) {
    yield call(handleErrorSaga, error as AxiosError);
  } finally {
    yield put(stopLoading());
  }
}

function* claimMovementsSaga(action: PayloadAction<CreateMovementPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const result = yield call(claimMovementsApi, action.payload);
    if (result.status === 'error') {
      yield call(handleErrorSaga, {
        message: result.errorMessage,
      } as AxiosError);
    } else {
      yield call(handleSuccessSaga, 'Movements claimed successfully');
    }
  } catch (error) {
    yield call(handleErrorSaga, error as AxiosError);
  } finally {
    yield put(stopLoading());
  }
}

function* rejectMovementsSaga(action: PayloadAction<UpdateMovementPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const success: boolean = yield call(rejectMovementsApi, action.payload);
    if (success) {
      yield call(handleSuccessSaga, 'Movements rejected successfully');
    }
  } catch (error) {
    yield call(handleErrorSaga, error as AxiosError);
  } finally {
    yield put(stopLoading());
  }
}

function* acceptMovementsSaga(action: PayloadAction<UpdateMovementPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const result = yield call(acceptMovementsApi, action.payload);
    if (result.status === 'error') {
      yield call(handleErrorSaga, {
        message: result.errorMessage,
      } as AxiosError);
    } else {
      yield call(handleSuccessSaga, 'Movements accepted successfully');
    }
  } catch (error) {
    yield call(handleErrorSaga, error as AxiosError);
  } finally {
    yield put(stopLoading());
  }
}

export function* watchMovementSagas(): SagaIterator {
  yield takeLatest(fetchUnassignedMovements, fetchMovementsSaga);
  yield takeLatest(fetchProposedMovements, fetchMovementAssignmentsSaga);
  yield takeLatest(claimMovements, claimMovementsSaga);
  yield takeLatest(rejectMovements, rejectMovementsSaga);
  yield takeLatest(acceptMovements, acceptMovementsSaga);
}
